import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { ImageSourcePropType, View } from 'react-native'

import styled from 'styled-components/native'

import { CARE_ADVISOR_EMAIL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { UnsubscribeEmailDog } from '../../../assets/microsite'
import { BodyText } from '../../../atoms/bodyText/BodyText'
import { Display } from '../../../atoms/display/Display'
import { Image } from '../../../atoms/image/Image'
import { Link } from '../../../atoms/link/Link'
import { PrimaryButton } from '../../../atoms/primaryButton/PrimaryButton'
import { CareNavigatorCareOptionsFooter } from '../../../molecules/careNavigatorCareOptionsFooter/CareNavigatorCareOptionsFooter'
import { BodyTextSize, DisplaySize } from '../../../styles/typeStyles'
import { HorizontalCenteredView } from '../../../templates/content/CommonViews'
import { openUrl } from '../../../utils/expo-linking'
import { tID } from '../../../utils/utils'

export interface UnsubscribePageProps {
  type: UnsubscribePageType
  careTeamEmail: string
  onContactCareNavigatorPress: () => void
  onEditNotificationSettingsPress: () => void
}

const UnsubscribeEmailDogImage = styled(Image)({
  height: '174px',
  width: '225px',
})

const Container = styled(HorizontalCenteredView)({
  marginTop: '48px',
  marginBottom: '24px',
})

const UnsubscribeContainer = styled(View)({
  maxWidth: '624px',
  gap: '32px',
  flexDirection: 'column',
  alignItems: 'center',
  paddingHorizontal: '48px',
})

const EditNotificationSettingsButton = styled(PrimaryButton)({
  alignSelf: 'center',
})

const ContactCareNavigatorFooter = styled(CareNavigatorCareOptionsFooter)({
  marginTop: '48px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
})

export enum UnsubscribePageType {
  promotion = 'promotion',
  outcomes = 'outcomes',
}

export const UnsubscribePage: FunctionComponent<UnsubscribePageProps> = ({
  type,
  careTeamEmail = CARE_ADVISOR_EMAIL,
  onContactCareNavigatorPress,
  onEditNotificationSettingsPress,
}) => {
  const { formatMessage } = useIntl()

  const unsubscribedHeader = formatMessage({
    defaultMessage: 'Your email has been unsubscribed.',
    description: 'Header text for the unsubscribed page',
  })

  const unsubscribedBody = formatMessage({
    defaultMessage:
      'You’ll still receive important communication from us, like session reminders and provider messages.',
    description: 'Body text for the unsubscribed page',
  })

  const outcomesBody = formatMessage(
    {
      defaultMessage:
        'If you would like to receive assessment emails in the future, email <link>{careTeamEmail}</link>.',
      description: 'Body text for the unsubscribed page',
    },
    {
      careTeamEmail,
      link: (careTeamEmail: string) => (
        <Link text={careTeamEmail} onPress={() => openUrl(`mailto:${careTeamEmail}`)} size={BodyTextSize.SMALL} />
      ),
    },
  )

  const bodyText = type === UnsubscribePageType.outcomes ? outcomesBody : unsubscribedBody

  return (
    <Container testID={tID('UnsubscribePromotionalPage')}>
      <UnsubscribeContainer>
        <Display
          size={DisplaySize.SMALL}
          text={unsubscribedHeader}
          textAlign='center'
          testID={tID('UnsubscribePromotionalPage-header')}
        />
        <BodyText text={bodyText} textAlign='center' testID={tID('UnsubscribePromotionalPage-body')} />
        <UnsubscribeEmailDogImage source={UnsubscribeEmailDog as ImageSourcePropType} />
        <EditNotificationSettingsButton
          onPress={onEditNotificationSettingsPress}
          text={formatMessage({
            defaultMessage: 'Edit notification settings',
            description: 'Button to take user to notification settings',
          })}
          testID={tID('UnsubscribePromotionalPage-EditNotificationSettingsButton')}
        />
      </UnsubscribeContainer>
      <ContactCareNavigatorFooter
        careNavigatorLinkTestId={tID('UnsubscribePromotionalPage-CareNavigatorLink')!}
        onContactCareNavigatorPress={onContactCareNavigatorPress}
        subheadTextAlign='center'
      />
    </Container>
  )
}
